<template>
    <app-layout>
       <Head title="Upgrade"/>
        <div class="bg-red-100 border-t border-b border-red-500 text-red-700 px-4 py-3 text-center m-4" v-if="$page.props.trial_expired">
            <div v-if="$page.props.previously_subscribed">
                You are no longer subscribed to a TextSpot plan. Please pick a plan to start sending messages again.
            </div>
            <div v-else>
                Your trial has expired.
            </div>
        </div>

        <div class="container mx-auto px-4 py-12 md:py-16 max-w-6xl">
            <!-- Header -->
            <div class="text-center mb-16">
                <h1 class="text-4xl font-bold tracking-tight sm:text-5xl mb-4">Simple, transparent pricing</h1>
                <p class="text-xl text-gray-600 max-w-2xl mx-auto">
                    Choose the perfect plan for your messaging needs. All plans include the same great features.
                </p>
            </div>

            <!-- Billing cycle selector -->
            <div class="flex justify-center mb-12">
                <div class="bg-gray-200 p-1 rounded-lg inline-flex border border-gray-300">
                    <button 
                        @click="showAnnualPrices = false"
                        :class="[
                            'px-6 py-2 rounded-md font-medium transition-all duration-200',
                            !showAnnualPrices 
                                ? 'bg-white text-gray-900 shadow-sm border border-gray-200' 
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        ]"
                    >
                        Monthly
                    </button>
                    <button 
                        @click="showAnnualPrices = true"
                        :class="[
                            'px-6 py-2 rounded-md font-medium transition-all duration-200 relative',
                            showAnnualPrices 
                                ? 'bg-white text-gray-900 shadow-sm border border-gray-200' 
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        ]"
                    >
                        Annual
                        <span class="ml-2 text-xs text-white rounded-full bg-blue-400 px-2 py-1">2 months free</span>
                    </button>
                </div>
            </div>

            <!-- Current plan display -->
            <div class="mb-12" v-if="activePlan">
                <div class="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-md mx-auto text-center">
                    <h3 class="font-semibold text-blue-900 mb-2">Your Current Plan</h3>
                    <template v-if="$page.props.paused">
                        <p class="text-blue-700">
                            <strong>Paused</strong> for ${{ (activePlan.price / 100).toLocaleString('en-us') }} per {{ activePlan.interval }}
                        </p>
                    </template>
                    <template v-else>
                        <p class="text-blue-700">
                            {{ activePlan.limit.toLocaleString() }} credits per {{ activePlan.interval }} at ${{ (activePlan.price / 100).toLocaleString('en-us') }} per {{ activePlan.interval }}
                        </p>
                    </template>
                </div>
            </div>

            <!-- Pricing selector -->
            <div class="max-w-4xl mx-auto mb-16">
                <div class="bg-white rounded-lg shadow-lg border border-gray-200">
                    <div class="p-6 border-b border-gray-200">
                        <h2 class="text-2xl font-bold mb-2">Select your {{ showAnnualPrices ? 'annual' : 'monthly' }} credit volume</h2>
                        <p class="text-gray-600">
                            Drag the slider to select how many credits you plan to use each {{ showAnnualPrices ? 'year' : 'month' }}. Upgrade or downgrade any time.
                        </p>
                    </div>
                    <div class="p-6">
                        <div class="mb-8">
                            <plan-slider 
                                v-model="selectedPlanIndex"
                                :plans="selectedPlans"
                                @plan-selected="selectedPlan = $event.lookup_key"
                            />
                        </div>

                        <!-- Selected plan details -->
                        <div class="bg-gray-50 p-6 rounded-lg" v-if="currentSelectedPlan">
                            <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                                <div>
                                    <h3 class="text-2xl font-bold">{{ currentSelectedPlan.limit.toLocaleString() }} credits{{ showAnnualPrices ? ' per year' : ' per month' }}</h3>
                                </div>
                                <div class="mt-4 md:mt-0 text-right">
                                    <div class="text-3xl font-bold">${{ getPrice(currentSelectedPlan.price) }}</div>
                                    <p class="text-gray-600">{{ currentSelectedPlan.overage_rate }}¢ per overage credit</p>
                                </div>
                            </div>
                            <jet-button 
                                class="w-full justify-center"
                                @click="showGetStartedDialog(currentSelectedPlan)"
                                :disabled="activePlan && activePlan.lookup_key === currentSelectedPlan.lookup_key"
                            >
                                <template v-if="activePlan && activePlan.lookup_key === currentSelectedPlan.lookup_key">
                                    Current Plan
                                </template>
                                <template v-else-if="activePlan">
                                    Review Plan Change
                                </template>
                                <template v-else>
                                    Get Started
                                </template>
                            </jet-button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Plan comparison table -->
            <div class="max-w-5xl mx-auto mb-16">
                <h2 class="text-3xl font-bold text-center mb-8">Compare All Plans</h2>
                <div class="overflow-x-auto bg-white rounded-lg shadow-lg border border-gray-200">
                    <table class="w-full">
                        <thead class="bg-gray-50">
                            <tr class="border-b border-gray-200">
                                <th class="text-left py-4 px-6 font-semibold">{{ showAnnualPrices ? 'Annual' : 'Monthly' }} Credits</th>
                                <th class="text-right py-4 px-6 font-semibold">{{ showAnnualPrices ? 'Annual' : 'Monthly' }} Price</th>
                                <th class="text-right py-4 px-6 font-semibold">Overage Cost</th>
                                <th class="text-right py-4 px-6"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr 
                                v-for="(plan, index) in selectedPlans" 
                                :key="plan.lookup_key"
                                :class="[
                                    'border-b border-gray-100 hover:bg-gray-50 transition-colors',
                                    index === selectedPlanIndex ? 'bg-blue-50' : ''
                                ]"
                            >
                                <td class="py-4 px-6 font-medium">
                                    {{ plan.limit.toLocaleString() }}
                                    <span v-if="plan.popular" class="ml-2 bg-orange-400 text-white text-xs px-2 py-1 rounded-full">
                                        Popular
                                    </span>
                                </td>
                                <td class="text-right py-4 px-6">${{ getPrice(plan.price) }}</td>
                                <td class="text-right py-4 px-6">{{ plan.overage_rate }}¢ per credit</td>
                                <td class="text-right py-4 px-6">
                                    <button
                                        @click="selectPlanFromTable(plan, index)"
                                        :disabled="activePlan && activePlan.lookup_key === plan.lookup_key"
                                        :class="[
                                            'px-4 py-2 rounded-md font-medium text-sm transition-colors',
                                            activePlan && activePlan.lookup_key === plan.lookup_key
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : index === selectedPlanIndex 
                                                    ? 'bg-orange-400 text-white' 
                                                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                        ]"
                                    >
                                        <template v-if="activePlan && activePlan.lookup_key === plan.lookup_key">
                                            Current Plan
                                        </template>
                                        <template v-else>
                                            {{ index === selectedPlanIndex ? 'Selected' : 'Select' }}
                                        </template>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Features section -->
            <div class="max-w-4xl mx-auto mb-16">
                <h2 class="text-3xl font-bold text-center mb-8">All Plans Include</h2>
                <div class="grid md:grid-cols-2 gap-6">
                    <div v-for="feature in features" :key="feature.name" class="flex p-6 border border-gray-200 rounded-lg bg-white">
                        <div class="mr-4 mt-1">
                            <font-awesome-icon :icon="['fas', 'check']" class="h-5 w-5 text-green-500" />
                        </div>
                        <div>
                            <h3 class="font-medium mb-2">{{ feature.name }}</h3>
                            <p class="text-gray-600">{{ feature.description }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- How it Works and FAQ Section -->
            <div class="grid md:grid-cols-1 gap-8 max-w-6xl mx-auto">
                <!-- FAQ Section -->
                <div class="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
                    <h2 class="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
                    <div class="space-y-4">
                        <div v-for="faq in faqs" :key="faq.question" class="border-b border-gray-200 pb-4 last:border-b-0">
                            <h3 class="font-medium mb-2">{{ faq.question }}</h3>
                            <p class="text-gray-600" v-html="faq.answer"></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <preview-invoice-modal
            :is-open="showInvoiceModal"
            :invoice="invoice"
            :license-key="selectedPlan"
            :is-scheduled-change="isScheduledChange"
            :schedule-date="scheduledDate"
            :new-plan-price="newPlanPrice"
            @close="showInvoiceModal = false"
        ></preview-invoice-modal>

        <get-started-modal
            :is-open="showGetStartedModal"
            :selected-plan="currentSelectedPlan"
            :show-annual-prices="showAnnualPrices"
            @close="showGetStartedModal = false"
            @continue="handleGetStartedContinue"
        ></get-started-modal>

        <pending-changes-confirm-modal
            :is-open="showPendingChangesModal"
            :pending-changes="pendingChanges"
            :processing="processingPendingChanges"
            :keep-button-text="keepButtonText"
            :continue-button-text="continueButtonText"
            @close="showPendingChangesModal = false"
            @confirmed="handlePendingChangesConfirmed"
        ></pending-changes-confirm-modal>
    </app-layout>
</template>

<script>
import AppLayout from "../../Layouts/AppLayout";
import JetButton from "../../Jetstream/Button";
import { Head } from '@inertiajs/vue3';

import PreviewInvoiceModal from "../../components/PreviewInvoiceModal";
import PlanSlider from '../../components/PlanSlider';
import GetStartedModal from '@/components/Modals/GetStartedModal';
import PendingChangesConfirmModal from '@/components/Modals/SubscriptionManagement/PendingChangesConfirmModal';

export default {
    name: "Upgrade",
    props: [],
    components: { 
        PreviewInvoiceModal, 
        AppLayout, 
        JetButton, 
        PlanSlider,
        GetStartedModal,
        PendingChangesConfirmModal,
        Head 
    },
    mounted() {
        this.getPlans();
    },
    data() {
        return {
            selectedPlan: "",
            activePlan: null,
            plans: {},
            planLevel: "paid",
            stripeKey: "",
            showAnnualPrices: false,
            redirectingToStripe: false,
            invoice: {},
            showInvoiceModal: false,
            showGetStartedModal: false,
            showPendingChangesModal: false,
            pendingChanges: [],
            processingPendingChanges: false,
            keepButtonText: 'Keep Pending Changes',
            continueButtonText: 'Cancel Pending & Continue Preview',
            selectedPlanIndex: 3, // Default to Growth plan equivalent
            isScheduledChange: false,
            scheduledDate: null,
            newPlanPrice: null,
            features: [
                { name: "All Features", description: "You get access to everything we've built and will build." },
                { name: "Free Incoming Texts", description: "Never pay for the messages you receive to your TextSpot phone number." },
                { name: "Delivery Tracking", description: "Real-time tracking of message delivery status." },
                { name: "Unlimited Contacts", description: "Import as many contacts as you'd like to TextSpot." },
                { name: "Private Phone Number", description: "Get your own local phone number (toll-free by request)." },
                { name: "Cancel or Pause Anytime", description: "Cancel your subscription or pause your account to keep your number and registration status active." },
                { name: "Chat Support", description: "Access to updated articles and real humans." },
                { name: "Integrations", description: "Connect with 7,000+ apps to automate sending messages and syncing data to and from TextSpot." }
            ],
            faqs: [
                {
                    question: "What happens if I exceed my monthly credit limit?",
                    answer: "If you exceed your monthly credit limit, you'll be charged a per-credit rate for any additional credits. The rate is based on your current plan's price per credit."
                },
                {
                    question: "Can I upgrade or downgrade my plan at any time?",
                    answer: "Yes, you can upgrade your plan at any time, and the new pricing will be prorated for the remainder of your billing cycle. Downgrades take effect at the start of your next billing cycle."
                },
                {
                    question: "Do unused credits roll over to the next month?",
                    answer: "No, unused credits do not roll over to the next month. Your credit allocation resets at the beginning of each billing cycle."
                },
                {
                    question: "How do credits work?",
                    answer: '<a href="https://help.textspot.io/en/articles/9117502-what-are-credits-and-how-do-they-work" target="_blank" class="text-blue-400 underline">Learn more about how credits work</a>'
                },
                {
                    question: "What is a message segment?", 
                    answer: '<a href="https://help.textspot.io/en/articles/9117497-what-is-a-message-segment" target="_blank" class="text-blue-400 underline">Learn about message segments</a>'
                }
            ]
        }
    },
    computed: {
        checkoutStatus: function() {
            let params = new URLSearchParams(document.location.search);
            if (params.has("checkout")) {
                return params.get("checkout");
            }
            return null;
        },
        selectedPlans() {
            if (this.showAnnualPrices) {
                return this.plans.year;
            }
            return this.plans.month
        },
        currentSelectedPlan() {
            if (this.selectedPlans && this.selectedPlans[this.selectedPlanIndex]) {
                return this.selectedPlans[this.selectedPlanIndex];
            }
            return null;
        }
    },
    methods: {
        getPlans() {
            axios
                .get(route("subscriptions.plans"))
                .then((response) => {
                    this.plans = response.data.plans;
                    this.stripeKey = response.data.stripe_key;
                    this.activePlan = response.data.active_plan;
                })
                .catch(function(error) {
                    console.log(error);
                });
        },
        async handlePlanSelection(plan) {
            this.selectedPlan = plan.lookup_key;

            if (this.$page.props.pause_scheduled) {
                if (window.confirm("You have your account scheduled to be paused. Switching plans will cancel the pause, continue?")) {

                    let response = await axios.post(route('subscriptions.cancel-pause')); 
                    this.$toasted.show(response.data.message, {
                        position: "bottom-right",
                        type: response.data.status,
                        duration: 3000
                    });
                    
                    if (response.data.status != "success") {
                        return;
                    }
                } else {
                    return;
                }
            }

            if (this.activePlan) {
                this.finalize();
            } else {
                this.generateCheckoutSession(plan)
            }
        },
        finalize(plan, forceCancelPending = false) {
            axios.post(route('upgrade.preview'), {
                lookup_key: this.selectedPlan,
                force_cancel_pending: forceCancelPending
            })
            .then((response) => {
                // Check if there are pending changes that need confirmation
                if (response.data.requires_confirmation && !forceCancelPending) {
                    this.pendingChanges = response.data.pending_changes;
                    this.keepButtonText = response.data.keep_button_text;
                    this.continueButtonText = response.data.continue_button_text;
                    this.showPendingChangesModal = true;
                    return;
                }

                // Check if this is a scheduled change (no invoice returned)
                if (response.data.scheduled) {
                    // This is a scheduled change - show modal without invoice
                    this.invoice = null;
                    this.isScheduledChange = true;
                    this.scheduledDate = response.data.scheduled_date;
                    this.newPlanPrice = response.data.new_plan_price;
                    this.showInvoiceModal = true;
                } else if (response.data.invoice) {
                    // This is an immediate upgrade with invoice preview
                    this.invoice = response.data.invoice;
                    this.isScheduledChange = false;
                    this.showInvoiceModal = true;
                } else {
                    alert("There was an error calculating a preview of your new invoice, please contact support.");
                }
            })
            .catch(function (error) {
                alert("There was an error calculating a preview of your new invoice, please contact support.");
                console.log(error);
            });
        },
        generateCheckoutSession(plan) {
            this.redirectingToStripe = true;
            const referralCode = window.tolt_referral;
            axios
                .post(route("subscriptions.checkout"), {
                    lookup_key: this.selectedPlan,
                    tolt_referral: referralCode
                })
                .then((response) => {
                    try {
                      window.dataLayer.push({
                        event: "subscription-checkout"
                      });
                    } catch (e) {}
                    window.Stripe(this.stripeKey).redirectToCheckout({
                        sessionId: response.data.sessionId
                    });
                    this.redirectingToStripe = false;
                })
                .catch(function (error) {
                    console.log(error);
                    this.redirectingToStripe = false;
                });
        },
        selectPlanFromTable(plan, index) {
            this.selectedPlanIndex = index;
            this.selectedPlan = plan.lookup_key;
            // Show dialog when clicking Selected or when selecting a new plan
            if (index === this.selectedPlanIndex || !this.activePlan || this.activePlan.lookup_key !== plan.lookup_key) {
                this.showGetStartedDialog(plan);
            }
        },
        getPrice(price) {
            return (price / 100).toLocaleString('en-us');
        },
        getPricePerCredit(plan) {
            const price = this.getPrice(plan.price);
            return (parseFloat(price.toString().replace(',', '')) / plan.limit).toFixed(4);
        },
        showGetStartedDialog(plan) {
            if (this.activePlan && this.activePlan.lookup_key === plan.lookup_key) {
                return; // Don't show dialog for current plan
            }
            this.selectedPlan = plan.lookup_key;
            
            // If user has an active plan, show invoice preview modal
            // If new user, show get started modal
            if (this.activePlan) {
                this.finalize();
            } else {
                this.showGetStartedModal = true;
            }
        },
        handleGetStartedContinue(plan) {
            this.showGetStartedModal = false;
            this.handlePlanSelection(plan);
        },
        handlePendingChangesConfirmed() {
            this.processingPendingChanges = true;
            this.showPendingChangesModal = false;
            
            try {
                // Call finalize again with force_cancel_pending = true
                this.finalize(null, true);
            } finally {
                this.processingPendingChanges = false;
            }
        }
    }
};
</script>

<style>
div > label {
    font-weight: bold;
}

.progress-bar {
    display: flex;
    justify-content: space-between;
    background: none;
    position: relative;
}
.number {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #eee;
    line-height: 0.86;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    font-size: 12px;
    font-weight: bold;
    position: relative;
    z-index: 1;
}

.line {
    position: absolute;
    width: 100%;
    top: calc(50% - 2px);
    height: 4px;
    background: #eee;
}

.active {
    @apply bg-blue-500;
    color: #fff;
}

.circle-select {
    @apply border border-gray-500 ml-4;

    width: 12px;
    height: 12px;
    border-radius: 50%;
}

.selected .circle-select {
    @apply bg-blue-200 border-blue-700;
}

.toggle-checkbox:checked {
    @apply right-0 border-blue-600;
    right: 0;
}

.toggle-checkbox:checked + .toggle-label {
    @apply bg-blue-600;
}
</style>